import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Link, Route, Routes } from 'react-router-dom';
import { AuthProvider } from './shared';
// App-Shell: auf jeder Seite sichtbar → eager laden
import MaintenanceGate from './pages/MaintenanceGate';
import ScrollToTop from './components/ScrollToTop';
import AnnouncementBanner from './pages/AnnouncementBanner';
import Header from './pages/Header';
import SiteChrome from './pages/SiteChrome';
import NewsletterButtonConditional from './pages/NewsletterButtonConditional';
import HannoverPopup from './pages/HannoverPopup';
import SelectionToProject from './components/SelectionToProject';
import './App.css';
import './hero-slider.css';

// Route-Seiten per Code-Splitting (eigene Chunks → kleines Initial-Bundle)
const EventRegistrationPage = lazy(() => import('./EventRegistrationComplete'));
const SEPAPage = lazy(() => import('./SEPAPageNew'));
const AdminPage = lazy(() => import('./AdminPage'));
const EventCalendar = lazy(() => import('./EventCalendar'));
const DatenschutzPage = lazy(() => import('./DatenschutzPage'));
const ImpressumPage = lazy(() => import('./ImpressumPage'));
const KontaktPage = lazy(() => import('./KontaktPage'));
const AboutPage = lazy(() => import('./AboutPage'));
const HomePage = lazy(() => import('./pages/HomePage'));
const HolyfirePage = lazy(() => import('./pages/HolyfireMinistry'));
const AllPostsPage = lazy(() => import('./pages/AllPostsPage'));
const EventsPage = lazy(() => import('./pages/EventsPage'));
const EventDetailPage = lazy(() => import('./pages/EventDetailPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const SpendenPage = lazy(() => import('./pages/SpendenPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const InvitePage = lazy(() => import('./pages/InvitePage'));
const ChatWrapper = lazy(() => import('./pages/ChatWrapper'));
const EventCheckInWrapper = lazy(() => import('./pages/EventCheckInWrapper'));
const EventEditWrapper = lazy(() => import('./pages/EventEditWrapper'));
const ShopPage = lazy(() => import('./pages/ShopPage'));
const HannoverPage = lazy(() => import('./pages/HannoverPage'));
const CourseDetailPage = lazy(() => import('./pages/CourseDetailPage'));
const TestifyPage = lazy(() => import('./pages/TestifyPage'));
const BibleReaderPage = lazy(() => import('./pages/BibleReaderPage'));
const SharedBoardPage = lazy(() => import('./pages/SharedBoardPage'));
const SkriptPublicPage = lazy(() => import('./pages/SkriptPublicPage'));
const ScripturiaDesignPreview = lazy(() => import('./pages/scripturia/ScripturiaDesignPreview'));
const ScripturiaStart = lazy(() => import('./pages/scripturia/ScripturiaStart'));
const ScripturiaLogin = lazy(() => import('./pages/scripturia/ScripturiaLogin'));
const SpaceChooser = lazy(() => import('./pages/scripturia/SpaceChooser'));
const ScripturiaWorkspace = lazy(() => import('./pages/scripturia/ScripturiaWorkspace'));
const ScripturiaAdmin = lazy(() => import('./pages/scripturia/ScripturiaAdmin'));
const AdminSpaceChooser = lazy(() => import('./pages/scripturia/AdminSpaceChooser'));
const BibleStudyLanding = lazy(() => import('./pages/BibleStudyLanding'));
const ConfirmSupport = lazy(() => import('./pages/ConfirmSupport'));
const HebrewLearningPage = lazy(() => import('./pages/HebrewLearningPage'));
const GalleryPage = lazy(() => import('./pages/GalleryPage'));
const OrderTrackPage = lazy(() => import('./pages/OrderTrackPage'));
const OrderStatusPage = lazy(() => import('./pages/OrderStatusPage'));
const GalleriesOverviewPage = lazy(() => import('./pages/GalleriesOverviewPage'));
const VideosOverviewPage = lazy(() => import('./pages/VideosOverviewPage'));

const RouteFallback = () => (
  <div data-testid="route-loading" style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <div style={{ width: 34, height: 34, border: '3px solid rgba(0,0,0,0.12)', borderTopColor: '#c2410c', borderRadius: '50%', animation: 'routeSpin 0.7s linear infinite' }} />
    <style>{`@keyframes routeSpin{to{transform:rotate(360deg)}}`}</style>
  </div>
);

function App() {
  return (
    <AuthProvider>
      <Router>
        <ScrollToTop />
        <MaintenanceGate>
        <div className="app">
          <SiteChrome>
            <AnnouncementBanner />
            <Header />
          </SiteChrome>
          <main className="main">
            <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/beitraege" element={<AllPostsPage />} />
              <Route path="/einladen" element={<InvitePage />} />
              <Route path="/events" element={<EventsPage />} />
              <Route path="/kalender" element={<EventCalendar />} />
              <Route path="/galerie/:id" element={<GalleryPage />} />
              <Route path="/fotos/:id" element={<GalleryPage />} />
              <Route path="/galerien" element={<GalleriesOverviewPage />} />
              <Route path="/videos" element={<VideosOverviewPage />} />
              <Route path="/events/:id" element={<EventDetailPage />} />
              <Route path="/events/:id/register" element={<EventRegistrationPage />} />
              <Route path="/admin/events/:id/edit" element={<EventEditWrapper />} />
              <Route path="/admin/events/:id/check-in" element={<EventCheckInWrapper />} />
              <Route path="/checkin" element={<EventCheckInWrapper />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/spenden" element={<SpendenPage />} />
              <Route path="/sepa" element={<SEPAPage />} />
              <Route path="/datenschutz" element={<DatenschutzPage />} />
              <Route path="/kontakt" element={<KontaktPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/holyfire" element={<HolyfirePage />} />
              <Route path="/hannover" element={<HannoverPage />} />
              <Route path="/shop" element={<ShopPage />} />
              <Route path="/testify" element={<TestifyPage />} />
              <Route path="/hebrew" element={<HebrewLearningPage />} />
              <Route path="/bible" element={<BibleReaderPage />} />
              <Route path="/board/:token" element={<SharedBoardPage />} />
              <Route path="/skript/:token" element={<SkriptPublicPage />} />
              <Route path="/scripturia-preview" element={<ScripturiaDesignPreview />} />
              <Route path="/scripturia" element={<ScripturiaStart />} />
              <Route path="/scripturia/login" element={<ScripturiaLogin />} />
              <Route path="/scripturia/workspace" element={<ScripturiaWorkspace />} />
              <Route path="/scripturia/admin" element={<ScripturiaAdmin />} />
              <Route path="/admin-auswahl" element={<AdminSpaceChooser />} />
              <Route path="/willkommen" element={<SpaceChooser />} />
              <Route path="/bibelstudium" element={<BibleStudyLanding />} />
              <Route path="/unterstuetzung-bestaetigen" element={<ConfirmSupport />} />
              <Route path="/courses/:id" element={<CourseDetailPage />} />
              <Route path="/track/:orderId" element={<OrderTrackPage />} />
              <Route path="/sendung/:token" element={<OrderStatusPage />} />

              <Route path="/impressum" element={<ImpressumPage />} />

              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/chat" element={<ChatWrapper />} />

              <Route path="/admin" element={<AdminPage />} />
            </Routes>
            </Suspense>
            
            {/* Newsletter-Button - nur auf öffentlichen Seiten */}
            <NewsletterButtonConditional />

          </main>
          <SiteChrome>
          <footer className="footer">
            <div className="container">
              <div className="footer-content">
                <div className="footer-column">
                  <h3>Holyfire Ministry gUG</h3>
                  <p>Royberg 2c, 21271 Hanstedt</p>
                  <p>Amtsgericht 21335 Lüneburg</p>
                  <p>HRB 212010</p>
                  <p>E-Mail: info@holy-fire.de</p>
                </div>
                <div className="footer-column">
                  <h3>Rechtliches</h3>
                  <Link to="/datenschutz">Datenschutz</Link>
                  <Link to="/impressum">Impressum</Link>
                  <Link to="/kontakt">Kontakt</Link>
                </div>
                <div className="footer-column">
                  <h3>Folgen Sie uns</h3>
                  <a href="https://www.youtube.com/@TESTIFY-TV" target="_blank" rel="noopener noreferrer">Testify TV</a>
                  <a href="https://www.youtube.com/@holyfire-tv" target="_blank" rel="noopener noreferrer">Holyfire TV</a>
                  <a href="https://www.youtube.com/@holy-fire44" target="_blank" rel="noopener noreferrer">Sound of Heaven</a>
                </div>
              </div>
              <div className="footer-bottom">
                <p>&copy; 2026 Holyfire Ministry gUG (haftungsbeschränkt) • Alle Rechte vorbehalten</p>
              </div>
            </div>
          </footer>
          </SiteChrome>
          <HannoverPopup />
          <SelectionToProject containerSelector={null} />
        </div>
        </MaintenanceGate>
      </Router>
    </AuthProvider>
  );
}

export default App;// Cache bust: 1773663246
